import React, { useEffect, useLayoutEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import classes from './physician.module.scss';
import Button from '../../../_common/Form/Buttons/Button';
import { getPhysiciansRequest } from '../../../../store/actions/physicians';
import Loader from '../../../_common/Loader/Loader';
import Info from '../../../_common/Info/Info';
import Tabs from '../../../_common/Tabs/Tabs';
import { usePhysicianTabs } from './usePhysicianTabs/usePhysicianTabs';
import { getHealthRequest } from '../../../../store/actions/healths';
import accountHelpers from '../../../../utils/accountHelpers';

const chartTemplate = {
  chart: {
    height: 280,
    type: 'area',
    toolbar: false,
  },
  dataLabels: {
    enabled: false,
  },
  series: [
    {
      name: 'Series',
      data: [],
    },
  ],
  fill: {
    type: 'gradchartient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.7,
      opacityTo: 0.9,
      stops: [0, 90, 100],
    },
  },

  tooltip: {
    style: {
      fontSize: '12px',
      fontFamily: 'Inter, sans-serif',
    },
    y: {
      formatter(val) {
        return val.toFixed(2);
      },
    },
  },

  yaxis: {
    tickAmount: 5,
    labels: {
      style: {
        colors: ['rgba(29, 39, 64, 0.5)'],
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        cssClass: 'apexcharts-yaxis-label',
      },
      formatter(val) {
        return val.toFixed(2);
      },
    },
  },
  xaxis: {
    categories: [],
  },
};

const Physician = ({ type }) => {
  const { pathname } = useLocation();
  const { physicianId } = useParams();

  const { tabs, requestData } = usePhysicianTabs(type);
  const dispatch = useDispatch();
  const physiciansList = useSelector((store) => store.physicians.physiciansList);
  const healthList = useSelector((store) => store.healths.healthList);

  const navigate = useNavigate();
  const isGroupType = type === 'group';
  const isHealthType = type === 'health';

  const [chart, setChart] = useState(chartTemplate);
  const [currentTab, setCurrentTab] = useState('Scripts');
  const [loading, setLoading] = useState(true);

  const data = physicianId ? physiciansList[0] : healthList[0];

  const address = _.compact([data?.addressStreet, data?.addressCity,
    data?.addressState, data?.addressZipCode]);

  useLayoutEffect(() => {
    (async () => {
      setLoading(true);
      setCurrentTab('Scripts');

      if (type === 'health') {
        await dispatch(getHealthRequest(requestData));
      } else {
        await dispatch(getPhysiciansRequest(requestData));
      }
      setLoading(false);
    })();
  }, [type]);

  useEffect(() => {
    if (isGroupType) {
      (async () => {
        const groupByMonth = _.groupBy(physiciansList.map((p) => p.Scripts).flat(), 'month');

        const calculatedData = _.map(groupByMonth, (datum, month) => ({
          month: moment(month).format('MMM'),
          revenue: _.sumBy(datum, (d) => (
            (parseFloat(d.networkPay) || 0)
          + (parseFloat(d.patientPay) || 0)
          + (parseFloat(d.primInsPay) || 0)
          + (parseFloat(d.secInsPay) || 0))).toFixed(2),
        }));

        const date = moment();
        let month = date.month();
        const months = calculatedData.map((m) => (m.month)) || [];
        const resultMath = [];
        const chartData = [];
        let i = 0;

        while (month >= 0) {
          if (months.includes(date.format('MMM'))) {
            chartData.push(calculatedData[i]?.revenue);
            i += 1;
          } else {
            chartData.push(0);
          }
          resultMath.push(date.format('MMM'));
          date.subtract(1, 'month');
          month -= 1;
        }

        await setChart((prev) => ({
          ...prev,
          series: [{ name: 'Series', data: chartData.reverse() }],
          xaxis: { categories: resultMath.reverse() || [] },
        }));
      })();
    } else if (data) {
      (async () => {
        const date = moment();
        let month = date.month();

        const months = {};
        // const months = data.date
        //   ? data?.date?.map((d) => (moment(d, 'yyyy-MM-dd').format('MMM'))) || []
        //   : data?.revenue?.[0]?.map((d) => (moment(d, 'yyyy-MM-dd').format('MMM'))) || [];

        (data.date || data?.revenue?.[0] || []).forEach((d, index) => {
          months[moment(d, 'yyyy-MM-dd').format('MMM')] = data?.revenue[1][index];
        });

        const resultMath = [];
        const chartData = [];
        // let i = 0;

        while (month >= 0) {
          if (months[date.format('MMM')]) {
            // if (months.includes(date.format('MMM'))) {
            // if (data?.date) {
            //   chartData.push(data?.revenue.reverse()[i]);
            // } else {
            // chartData.push(data?.revenue[1][i]);
            // }

            // i += 1;

            chartData.push(months[date.format('MMM')]);
          } else {
            chartData.push(0);
          }

          resultMath.push(date.format('MMM'));
          date.subtract(1, 'month');
          month -= 1;
        }

        await setChart((prev) => ({
          ...prev,
          series: [{ name: 'Series', data: chartData.reverse() }],
          xaxis: { categories: resultMath.reverse() || [] },
        }));
      })();
    }
  }, [physiciansList, data]);

  const clickGroupName = (name) => {
    if (!isGroupType) {
      navigate(`/physicians/groups/${name}`);
    }
  };

  return (
    loading ? <Loader className={classes.physician_loader} />
      : (
        <div className={classes.physician_wrapper}>
          <div className={classes.header}>
            <div className={classes.header_title_clock}>
              <div className={!isGroupType ? classes.header_title : classes.group_header_title}>
                <div>
                  {!isGroupType && <h2>{isHealthType ? data.name : `${data?.firstName} ${data?.lastName}`}</h2>}

                  {!isHealthType && (
                  <p
                    onClick={() => clickGroupName(data?.group)}
                    style={!isGroupType
                      ? { cursor: 'pointer' }
                      : { cursor: 'default', pointerEvents: 'none' }}
                  >
                    {data?.group}
                  </p>
                  )}
                </div>

                <div style={isGroupType ? { marginBottom: '30px' } : null}>
                  {!isGroupType ? (
                    <ul>
                      <li>Name</li>
                      <li>{isHealthType ? 'HubId' : 'Group'}</li>
                      {!isHealthType && <li>Specialty</li>}
                    </ul>
                  ) : (
                    <ul>
                      <li>Name</li>
                      <li>Number of Physicians</li>
                    </ul>
                  )}

                  {!isGroupType ? (
                    <ul>
                      <li>{isHealthType ? data.name : `${data?.firstName} ${data?.lastName}`}</li>
                      <li>{isHealthType ? data.hubId : data?.group}</li>
                      {!isHealthType && <li>{data?.specialization}</li>}
                    </ul>
                  ) : (
                    <ul>
                      <li>{data?.group}</li>
                      <li>{physiciansList.length}</li>
                    </ul>
                  )}
                </div>
              </div>

              {((type === 'health' && accountHelpers.getUser().role !== 'Rep') || type !== 'health')
                && (
                <div className={!isGroupType ? classes.header_button : classes.group_header_button}>
                  {!isGroupType && accountHelpers.getUser().role !== 'Health' && (
                  <Button
                    title={type === 'health' ? 'Edit Health Plan' : 'Edit physician'}
                    editButton
                    className={classes.assign_btn}
                    type="button"
                    href={`${pathname}/edit`}
                  />
                  )}

                  {accountHelpers.getUser().role !== 'Health' && (
                  <Button
                    title="Give access"
                    className={classes.assign_btn}
                    unlockButton
                    type="button"
                    reverseColor
                    href={`${pathname}/access`}
                  />
                  )}
                </div>
                )}
            </div>

            <div className={!isGroupType ? classes.header_char_block : classes.group_header_char_block}>
              <div>
                <Chart
                  type={chart.chart.type}
                  options={chart}
                  series={chart.series}
                  width="100%"
                  height={170}
                />

              </div>

              {!isGroupType && (
                <div>
                  <p>{data?.phone ? data?.phone : '-'}</p>
                  <p>{(!_.isEmpty(address)) ? address.join(', ') : '-'}</p>
                  <p>{data?.email ? data?.email : '-'}</p>
                </div>
              )}

            </div>
          </div>

          {!isGroupType && (
          <div className={classes.main}>
            <div>
              <div>
                <ul>
                  {!isHealthType && <li>{'Physician\'s NPI'}</li>}
                  <li>Point of Contact</li>
                </ul>

                <ul>
                  {!isHealthType && <li>{data.NPI ? data.NPI : '-'}</li>}
                  <li>{data.contact ? data.contact : '-'}</li>
                </ul>
              </div>

              <div>
                <ul>
                  {!isHealthType && <li>{'Physician\'s DEA'}</li>}

                  {accountHelpers.getUser().role !== 'Health' && <li>Sales Rep</li>}
                </ul>

                <ul>
                  {!isHealthType && <li>{data.DEA ? data.DEA : '-'}</li>}

                  {accountHelpers.getUser().role !== 'Health' && (
                  <li>
                    {isHealthType
                      ? data?.Representative?.name ? data?.Representative?.name : '-'
                      : data.rep ? data.rep : '-'}
                  </li>
                  )}
                </ul>
              </div>
            </div>

            {!isHealthType && (
            <div>
              <Info
                title="Physician Warning"
                physicianWarning
              >
                {data?.physicianWarning || 'None'}
              </Info>
            </div>
            )}
          </div>
          )}

          <Tabs
            tabs={tabs}
            setCurrentTab={setCurrentTab}
            currentTab={currentTab}
            defaultLinePosition={isHealthType ? { left: '0px', right: '98px' } : { left: '0px', right: '401.5px' }}
          />

        </div>
      )

  );
};

Physician.propTypes = {
  type: PropTypes.string,
};

Physician.defaultProps = {
  type: '',
};

export default Physician;
